import type { ReactNode } from "react";

import { useDayOfWeekBreakdown } from "../api/hooks/useDayOfWeekBreakdown";
import { useHoldTimeBreakdown } from "../api/hooks/useHoldTimeBreakdown";
import { useInstrumentBreakdown } from "../api/hooks/useInstrumentBreakdown";
import { useOverallStatistics } from "../api/hooks/useOverallStatistics";
import { useStrategyBreakdown } from "../api/hooks/useStrategyBreakdown";
import { useTimeOfDayBreakdown } from "../api/hooks/useTimeOfDayBreakdown";
import type { components } from "../api/schema";
import {
  BreakdownTable,
  type BreakdownBucket,
} from "../components/breakdowns/BreakdownTable";
import { Skeleton } from "../components/ui/skeleton";
import { useGlobalFilters } from "../lib/globalFilters";
import { formatSignedMoney } from "../lib/money";

type OverallStatisticsResponse = components["schemas"]["OverallStatisticsResponse"];

function sortByNetPnLDesc<T extends BreakdownBucket>(
  buckets: ReadonlyArray<T>,
): ReadonlyArray<T> {
  return [...buckets].sort(
    (a, b) => Number(b.net_pnl_amount) - Number(a.net_pnl_amount),
  );
}

type Nullable = string | null | undefined;
type Tone = "pos" | "neg" | "neutral";

function toneClass(tone: Tone): string {
  if (tone === "pos") return "text-success";
  if (tone === "neg") return "text-danger";
  return "text-on-surface";
}

function signedTone(amount: Nullable): Tone {
  if (amount === null || amount === undefined) return "neutral";
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return "neutral";
  return n > 0 ? "pos" : "neg";
}

function signedMoneyOrDash(amount: Nullable, currency: Nullable): string {
  if (amount === null || amount === undefined) return "—";
  try {
    return formatSignedMoney(amount, { currency: currency ?? "USD" });
  } catch {
    return "—";
  }
}

function percentOrDash(decimal: Nullable): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function decimalOrDash(decimal: Nullable): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Overview KPI tile, mirroring the Trades-page KPI strip styling for app-wide
// consistency. Purely presentational.
function KpiTile({
  label,
  value,
  sub,
  tone = "neutral",
  bar,
  testId,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  tone?: Tone;
  bar?: number;
  testId?: string;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1 rounded-card bg-surface-container p-4" data-testid={testId}>
      <div className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
        {label}
      </div>
      <div className={`font-data text-2xl font-semibold tabular-nums ${toneClass(tone)}`}>
        {value}
      </div>
      {bar !== undefined ? (
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-container-low">
          <div
            className="h-full rounded-full bg-success"
            style={{ width: `${Math.round(Math.min(1, Math.max(0, bar)) * 100)}%` }}
          />
        </div>
      ) : null}
      {sub !== undefined ? <div className="text-[11px] text-on-surface-muted">{sub}</div> : null}
    </div>
  );
}

// The six headline KPIs that lead the Overview. Every value comes from the
// single overall-statistics query (Net P&L is the canonical total_net_pnl).
function OverviewKpiStrip({ stats }: { stats: OverallStatisticsResponse }): ReactNode {
  const { pnl, total_trades } = stats;
  const winRate = Number(pnl.win_rate);
  return (
    <section
      aria-label="Analytics summary"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
      data-testid="analytics-kpi-strip"
    >
      <KpiTile
        label="Net P&L"
        value={signedMoneyOrDash(pnl.total_net_pnl_amount, pnl.total_net_pnl_currency)}
        tone={signedTone(pnl.total_net_pnl_amount)}
        testId="analytics-kpi-net-pnl"
      />
      <KpiTile
        label="Win rate"
        value={percentOrDash(pnl.win_rate)}
        bar={Number.isFinite(winRate) ? winRate : 0}
        testId="analytics-kpi-win-rate"
      />
      <KpiTile
        label="Profit factor"
        value={decimalOrDash(pnl.profit_factor)}
        testId="analytics-kpi-profit-factor"
      />
      <KpiTile
        label="Expectancy"
        value={signedMoneyOrDash(pnl.expectancy_amount, pnl.expectancy_currency)}
        tone={signedTone(pnl.expectancy_amount)}
        testId="analytics-kpi-expectancy"
      />
      <KpiTile
        label="Avg R"
        value={decimalOrDash(pnl.average_r_multiple)}
        testId="analytics-kpi-avg-r"
      />
      <KpiTile
        label="Trades"
        value={String(total_trades.trade_count)}
        testId="analytics-kpi-trades"
      />
    </section>
  );
}

// Skeleton mirror of the analytics layout: header plus the two "What you
// trade" / "When you trade" sections, each a grid of breakdown-table cards.
function AnalyticsSkeleton() {
  return (
    <section
      className="space-y-8 p-6"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading analytics</span>
      <div className="space-y-2">
        <Skeleton className="h-8 w-44" />
        <Skeleton className="h-4 w-72" />
      </div>
      {[0, 1].map((section) => (
        <div key={section} className="space-y-3">
          <Skeleton className="h-4 w-32" />
          <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
            {[0, 1, 2].map((card) => (
              <Skeleton key={card} className="h-40 w-full rounded-card" />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

export function AnalyticsPage() {
  // The Analytics filter bar lives in the shared AnalyticsLayout; every section
  // reads the same URL filters so one bar narrows the whole surface.
  const filters = useGlobalFilters();
  const overall = useOverallStatistics(filters);
  const strategy = useStrategyBreakdown(filters);
  const instrument = useInstrumentBreakdown(filters);
  const dayOfWeek = useDayOfWeekBreakdown(filters);
  const timeOfDay = useTimeOfDayBreakdown(filters);
  const holdTime = useHoldTimeBreakdown(filters);

  const isLoading =
    overall.isLoading ||
    strategy.isLoading ||
    instrument.isLoading ||
    dayOfWeek.isLoading ||
    timeOfDay.isLoading ||
    holdTime.isLoading;

  const isError =
    overall.isError ||
    strategy.isError ||
    instrument.isError ||
    dayOfWeek.isError ||
    timeOfDay.isError ||
    holdTime.isError;

  const firstError =
    overall.error ??
    strategy.error ??
    instrument.error ??
    dayOfWeek.error ??
    timeOfDay.error ??
    holdTime.error;

  if (isLoading) {
    return <AnalyticsSkeleton />;
  }

  if (
    isError ||
    overall.data === undefined ||
    strategy.data === undefined ||
    instrument.data === undefined ||
    dayOfWeek.data === undefined ||
    timeOfDay.data === undefined ||
    holdTime.data === undefined
  ) {
    return (
      <section className="p-6" role="alert">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Analytics unavailable
        </h1>
        <p className="mt-2 text-on-surface-variant">
          {firstError instanceof Error
            ? firstError.message
            : "Failed to load analytics."}
        </p>
      </section>
    );
  }

  const stats = overall.data;

  // Zero trades for the current filter selection: lead with an import prompt
  // instead of a wall of empty breakdowns (operator-confirmed: any zero-trade
  // result shows this state, regardless of cause).
  if (stats.total_trades.trade_count === 0) {
    return (
      <section className="space-y-8 p-6">
        <header>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Analytics
          </h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Closed-trade breakdowns &mdash; what you trade, and when.
          </p>
        </header>
        <div
          className="flex flex-col items-center justify-center gap-2 rounded-card bg-surface-container px-6 py-16 text-center"
          data-testid="analytics-empty"
        >
          <p className="text-base font-medium text-on-surface">No analytics yet</p>
          <p className="max-w-sm text-sm text-on-surface-variant">
            Import trades to populate analytics.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-8 p-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">Analytics</h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Closed-trade breakdowns &mdash; what you trade, and when.
        </p>
      </header>

      <OverviewKpiStrip stats={stats} />

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-on-surface">What you trade</h2>
        <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
          <BreakdownTable
            title="By strategy"
            buckets={sortByNetPnLDesc(strategy.data.points)}
            currency={strategy.data.currency}
            testId="strategy-breakdown"
          />
          <BreakdownTable
            title="By instrument"
            buckets={sortByNetPnLDesc(instrument.data.points)}
            currency={instrument.data.currency}
            testId="instrument-breakdown"
          />
        </div>
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-on-surface">When you trade</h2>
        <div className="grid gap-x-8 gap-y-6 lg:grid-cols-2">
          <BreakdownTable
            title="By day of week"
            buckets={dayOfWeek.data.points}
            currency={dayOfWeek.data.currency}
            testId="day-of-week-breakdown"
          />
          <BreakdownTable
            title="By time of day"
            buckets={timeOfDay.data.points}
            currency={timeOfDay.data.currency}
            testId="time-of-day-breakdown"
          />
          <BreakdownTable
            title="By hold time"
            buckets={holdTime.data.points}
            currency={holdTime.data.currency}
            testId="hold-time-breakdown"
          />
        </div>
      </div>
    </section>
  );
}
